import React, { useState } from 'react';
import { userApi } from '../api';
import api from '../api';
import { ExternalLink, DatabaseZap, User as UserIcon, Shield, Eye, EyeOff, Users, CheckCircle, Loader2 } from 'lucide-react';

const PRIVACY_LEVELS = [
  { value: 'public', label: 'Public', icon: <Eye size={16} />, desc: 'Tout le monde peut voir votre profil' },
  { value: 'friends', label: 'Amis', icon: <Users size={16} />, desc: 'Seuls vos amis peuvent voir votre profil' },
  { value: 'private', label: 'Privé', icon: <EyeOff size={16} />, desc: 'Votre profil est entièrement masqué' },
];

const STATUS_LIST = [
  { key: 'watching', label: 'En cours' },
  { key: 'completed', label: 'Terminé' },
  { key: 'on_hold', label: 'En pause' },
  { key: 'dropped', label: 'Abandonné' },
  { key: 'plan_to_watch', label: 'À voir' },
];

const Settings = ({ user, onUserUpdate }) => {
  const [syncEnabled, setSyncEnabled] = useState(user?.auto_sync_mal || false);
  const [loading, setLoading] = useState(false);

  // Privacy state
  const [globalPrivacy, setGlobalPrivacy] = useState(user?.privacy_level || 'public');
  const [statusPrivacy, setStatusPrivacy] = useState(user?.privacy_settings || {});
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [privacySaved, setPrivacySaved] = useState(false);

  const handleConnectMal = () => {
    const token = localStorage.getItem('token');
    if (token) {
      window.location.href = `${api.defaults.baseURL}/auth/mal/authorize?token=${token}`;
    }
  };

  const handleToggleSync = async () => {
    setLoading(true);
    try {
      const newState = !syncEnabled;
      const res = await userApi.toggleMalSync(newState);
      setSyncEnabled(newState);
      if (onUserUpdate) onUserUpdate(res.data);
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la modification de la synchronisation.");
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectMal = async () => {
    if (!window.confirm("Voulez-vous vraiment déconnecter votre compte MyAnimeList ? Vos données existantes seront conservées, mais la synchronisation s'arrêtera.")) return;
    setLoading(true);
    try {
      await userApi.disconnectMal();
      if (onUserUpdate) {
        const res = await userApi.getMe();
        onUserUpdate(res.data);
      }
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la déconnexion.");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePrivacy = async () => {
    setSavingPrivacy(true);
    setPrivacySaved(false);
    try {
      const res = await userApi.updatePrivacy(globalPrivacy, statusPrivacy);
      if (onUserUpdate) onUserUpdate(res.data);
      setPrivacySaved(true);
      setTimeout(() => setPrivacySaved(false), 2500);
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la sauvegarde de la confidentialité.");
    } finally {
      setSavingPrivacy(false);
    }
  };

  const handleStatusPrivacyChange = (statusKey, value) => {
    setStatusPrivacy(prev => ({ ...prev, [statusKey]: value }));
  };

  return (
    <div className="max-w-4xl mx-auto p-6 mt-8 space-y-6">
      <h1 className="text-3xl font-bold text-white flex items-center gap-3">
        <UserIcon size={32} />
        Paramètres du Profil
      </h1>

      {/* === CONFIDENTIALITÉ === */}
      <div className="bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-700">
        <h2 className="text-xl font-semibold mb-6 border-b border-gray-700 pb-4 flex items-center gap-2">
          <Shield size={20} className="text-blue-400" />
          Confidentialité
        </h2>

        {/* Global profile visibility */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-300 mb-1">Visibilité globale du profil</h3>
          <p className="text-xs text-gray-500 mb-3">
            Si vous choisissez <strong className="text-gray-400">Privé</strong>, votre profil complet sera masqué, peu importe les réglages individuels.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {PRIVACY_LEVELS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setGlobalPrivacy(opt.value)}
                className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${
                  globalPrivacy === opt.value
                    ? 'border-blue-500/60 bg-blue-500/10 text-blue-300'
                    : 'border-gray-700 bg-gray-900/40 text-gray-400 hover:border-gray-600 hover:text-gray-300'
                }`}
              >
                <span className="mt-0.5">{opt.icon}</span>
                <div>
                  <div className="font-semibold text-sm">{opt.label}</div>
                  <div className="text-xs opacity-70 mt-0.5">{opt.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Per-status defaults (only relevant if profile is not entirely private) */}
        {globalPrivacy !== 'private' && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-300 mb-1">Visibilité par statut (par défaut)</h3>
            <p className="text-xs text-gray-500 mb-3">
              Définissez qui peut voir les animes selon leur statut. Vous pourrez toujours surcharger anime par anime depuis le tableau de bord.
            </p>
            <div className="space-y-2">
              {STATUS_LIST.map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between py-2 px-3 bg-gray-900/40 rounded-lg border border-gray-700/50">
                  <span className="text-sm text-gray-300 font-medium">{label}</span>
                  <div className="flex items-center gap-1">
                    {['public', 'friends', 'private'].map(level => (
                      <button
                        key={level}
                        onClick={() => handleStatusPrivacyChange(key, level)}
                        className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                          (statusPrivacy[key] || 'public') === level
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-gray-200'
                        }`}
                      >
                        {level === 'public' ? 'Public' : level === 'friends' ? 'Amis' : 'Privé'}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={handleSavePrivacy}
          disabled={savingPrivacy}
          className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-semibold disabled:opacity-60"
        >
          {savingPrivacy ? <Loader2 size={16} className="animate-spin" /> : privacySaved ? <CheckCircle size={16} /> : <Shield size={16} />}
          {privacySaved ? 'Sauvegardé !' : 'Sauvegarder'}
        </button>
      </div>

      {/* === MAL === */}
      <div className="bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-700">
        <h2 className="text-xl font-semibold mb-6 border-b border-gray-700 pb-4">
          Intégrations &amp; Synchronisation
        </h2>

        <div className="bg-gray-900/50 rounded-xl border border-gray-700/50 overflow-hidden">
          <div className="p-4 border-b border-gray-700/50 bg-[#2E51A2]/10">
            <h3 className="font-bold text-lg text-blue-400 flex items-center gap-2">
              Intégration MyAnimeList
            </h3>
            <p className="text-gray-400 text-sm mt-1 max-w-xl">
              Liez votre compte pour synchroniser automatiquement les épisodes regardés depuis l'extension et récupérer les dernières données.
            </p>
          </div>

          <div className="p-5 flex flex-col gap-6">
            
            {/* 1. Statut de connexion */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-6 border-b border-gray-800">
              <div>
                <span className="text-gray-200 font-medium">Statut du compte</span>
                <p className="text-sm text-gray-500 mt-1">Gérez la connexion à votre compte MyAnimeList.</p>
              </div>
              
              {!user?.mal_connected ? (
                <button
                  onClick={handleConnectMal}
                  className="bg-[#2E51A2] hover:bg-[#203c7a] px-6 py-2.5 rounded-lg flex items-center gap-2 transition-colors font-medium text-white whitespace-nowrap"
                >
                  Se connecter à MAL <ExternalLink size={16} />
                </button>
              ) : (
                <div className="flex flex-col sm:items-end gap-2">
                  {user?.mal_username && (
                    <span className="text-gray-300 text-sm">
                      Connecté en tant que <span className="text-blue-400 font-semibold">{user.mal_username}</span>
                    </span>
                  )}
                  <div className="flex items-center gap-3">
                    <span className="text-green-400 font-medium px-3 py-1.5 bg-green-400/10 rounded-full text-sm border border-green-400/20">
                      ✔ Connecté
                    </span>
                    <button
                      onClick={handleDisconnectMal}
                      disabled={loading}
                      className="text-red-400 hover:text-white hover:bg-red-500/20 px-4 py-1.5 rounded-lg text-sm border border-red-500/20 transition-colors font-medium"
                      title="Déconnecter MyAnimeList"
                    >
                      Déconnecter
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 2. Sync Auto */}
            {user?.mal_connected && (
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <span className="text-gray-200 font-medium flex items-center gap-2">
                    <DatabaseZap size={16} className={syncEnabled ? "text-blue-400" : "text-gray-500"}/>
                    Synchronisation Automatique
                  </span>
                  <p className="text-sm text-gray-500 mt-1">L'extension mettra à jour MAL automatiquement quand vous regardez un épisode.</p>
                </div>
                
                <label className="flex items-center cursor-pointer mt-2 sm:mt-0">
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      className="sr-only" 
                      checked={syncEnabled}
                      onChange={handleToggleSync}
                      disabled={loading}
                    />
                    <div className={`block w-12 h-7 rounded-full transition-colors ${syncEnabled ? 'bg-blue-500' : 'bg-gray-600'}`}></div>
                    <div className={`dot absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform ${syncEnabled ? 'transform translate-x-5' : ''}`}></div>
                  </div>
                </label>
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
};

export default Settings;

